import {
  EmptyState,
  IconButton,
  Page,
  Spinner,
  Tooltip,
} from '@dixa/lib-shared-habla';
import { useCallback, useEffect, useState } from 'react';
import { Waypoint } from 'react-waypoint';

import { auditLogsApi, UserActivityType } from 'apis/auditLogs';
import { useLazyPaginatedEndpoint } from 'utils/apis/pagination/useLazyPaginatedEndpoint';

import { isDateRangeValid } from './activityLog';
import styles from './activityLog.module.scss';
import { ActivityLogDateRangeFilter } from './filters/components/activityLogDateRangeFilter';
import { EventTypeFilter } from './filters/components/eventTypeFilter';
import { ComponentByUserLogType } from './userActivityTypes';
import { userEventTypeLabels } from './userActivityTypes/filters/userEventTypeLabels';

type UserActivityLogFiltersValue = {
  fromTs?: string;
  toTs?: string;
  eventTypes?: UserActivityType[];
};

type UserActivityLogProps = {
  /** The id of the contact/user whose activities are displayed. */
  userId: string;
};

/** Renders the "Activities" feed on a contact's details page. */
export function UserActivityLog({ userId }: UserActivityLogProps): JSX.Element {
  const [filters, setFilters] = useState<UserActivityLogFiltersValue>({});
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');

  const {
    items: entries,
    changeArg,
    asyncStatus,
    hasNextPage,
    nextPage,
  } = useLazyPaginatedEndpoint({
    endpoint: auditLogsApi.endpoints.readUserAuditLogs,
    itemsSelector: (response) => response.data.logs,
    infinite: true,
  });

  const hasActiveFilters = Boolean(
    filters.fromTs || filters.toTs || filters.eventTypes?.length,
  );
  const isDateRangeInvalid = !isDateRangeValid(filters.fromTs, filters.toTs);

  const handleClearFilters = useCallback(() => setFilters({}), []);

  const clearFiltersButtonProps = hasActiveFilters
    ? {
        children: 'Clear filters',
        variant: 'secondary' as const,
        onClick: handleClearFilters,
      }
    : undefined;

  useEffect(() => {
    if (isDateRangeValid(filters.fromTs, filters.toTs)) {
      changeArg({
        resourceId: userId,
        limit: 25,
        sort: { key: 'timestamp', direction: sortDirection },
        fromTs: filters.fromTs,
        toTs: filters.toTs,
        eventTypes: filters.eventTypes,
      });
    }
  }, [changeArg, userId, filters, sortDirection]);

  return (
    <Page.Section
      className={styles.section}
      title="Activities"
      controls={
        <>
          <ActivityLogDateRangeFilter
            fromTs={filters.fromTs}
            toTs={filters.toTs}
            onChange={(changed) =>
              setFilters((current) => ({ ...current, ...changed }))
            }
          />
          <EventTypeFilter
            labels={userEventTypeLabels}
            value={filters.eventTypes ?? []}
            onChange={(eventTypes) =>
              setFilters((current) => ({ ...current, eventTypes }))
            }
          />
          <Tooltip
            content={sortDirection === 'desc' ? 'Newest first' : 'Oldest first'}
          >
            <IconButton
              variant="tertiary"
              icon={sortDirection === 'asc' ? 'filter-z-a' : 'filter-a-z'}
              aria-label={
                sortDirection === 'desc'
                  ? 'Sorted newest first'
                  : 'Sorted oldest first'
              }
              onClick={() =>
                setSortDirection((current) =>
                  current === 'desc' ? 'asc' : 'desc',
                )
              }
            />
          </Tooltip>
        </>
      }
    >
      <div className={styles.root}>
        {isDateRangeInvalid ? (
          <EmptyState
            icon="calendar-times"
            title="Invalid date range"
            description="The start date is after the end date. Adjust the range to see the activities."
            buttonProps={clearFiltersButtonProps}
          />
        ) : (
          <>
            {asyncStatus === 'rejected' && (
              <EmptyState
                icon="exclamation-triangle-filled"
                title="Failed to retrieve customer activities"
                description="Something went wrong while loading the activity feed."
              />
            )}

            {entries.map((entry) => (
              <ComponentByUserLogType key={entry.event_id} entry={entry} />
            ))}

            {asyncStatus === 'fulfilled' && !entries.length && (
              <EmptyState
                icon="file-text-search"
                title="No activities found"
                description={
                  hasActiveFilters
                    ? 'No activity matches the selected filters. Try clearing them to see more.'
                    : 'There are no activities to display for this contact.'
                }
                buttonProps={clearFiltersButtonProps}
              />
            )}

            {(asyncStatus === 'pending' || asyncStatus === 'uninitialized') && (
              <Spinner />
            )}

            {asyncStatus === 'fulfilled' && hasNextPage && (
              <Waypoint onEnter={nextPage} />
            )}
          </>
        )}
      </div>
    </Page.Section>
  );
}
